/**
 * Property Dossier context — the asset, the sponsor, the sponsor's book and
 * the comparable set behind one deal, plus the external site intelligence for
 * its address.
 *
 * A loan row is a deal, not an asset. The property and sponsor masters, the
 * comp database and the public location sources hold the rest of what the
 * dossier prints, and none of it reached the screen before: pages rendered
 * "Not on record" over records the platform already had. These two calls are
 * the supply.
 *
 * `getContext` is a database read and is fast. `getSiteIntel` fans out to
 * Google and three US government services and is cached server-side for hours,
 * so it loads beside the record rather than gating it. Every intel figure
 * carries the source that produced it, and every source that could not answer
 * carries its reason — a caller can always tell "the source said no" from
 * "the source was never asked".
 */

import api from '@/services/api'

export type DossierLinkBasis = 'id' | 'name' | null

export type DossierPropertyMaster = {
  id: string
  name: string
  address: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  metro: string | null
  submarket: string | null
  lat: number | null
  lng: number | null
  assetType: string
  assetSubtype: string | null
  yearBuilt: number | null
  yearRenovated: number | null
  stories: number | null
  units: number | null
  squareFeet: number | null
  lotSizeAcres: number | null
  parkingSpaces: number | null
  currentOccupancy: number | null
  occupancyHistory: Record<string, unknown>[]
  operatingHistory: Record<string, unknown>[]
  rentRoll: Record<string, unknown>[]
  externalRefs: Record<string, unknown>
  enrichment: Record<string, unknown>
  enrichmentUpdatedAt: string | null
  notes: string | null
}

/** Where an address part came from. Strongest first: a person's edit, what
 *  the package states, the master. `places` marks a row an old name lookup
 *  wrote; the server withholds those and nothing selects them. */
export type DossierAddressSource = 'person' | 'package' | 'places' | 'master'

/**
 * The deal's address as the server settles it (`services/address_precedence`):
 * person-entered, then package-stated, then the property master — with where
 * each input came from, so the browser can run the same rule over values it
 * has fresher than this response.
 *
 * Every key but the address parts is read as optional, so an older server that
 * predates a key still parses. The keys here are exactly the ones
 * `AddressBlock.to_dict()` and `dossier_context._address_block` emit; the server
 * sends no per-address verdict, so the browser derives one from the parts and
 * where each came from (`assetCoordinate.resolveAssetPoint`).
 */
export type DossierAddress = {
  street: string | null
  city: string | null
  state: string | null
  zip: string | null
  full: string
  location: string | null
  source: DossierAddressSource | null
  /** Always false: the server withholds a name lookup's street instead of suggesting it. */
  suggested?: boolean
  /** Always false, for the same reason. */
  needsConfirmation?: boolean
  partSources?: Partial<Record<'street' | 'city' | 'state' | 'zip', DossierAddressSource>>
  dealSources?: Partial<Record<'street' | 'city' | 'state' | 'zip', DossierAddressSource>>
  masterRank?: DossierAddressSource | null
  locationSource?: DossierAddressSource
  /** False when the master's coordinate was resolved for some other address. */
  masterPointApplies?: boolean
  /** Parts a name lookup wrote that the server set aside. */
  withheld?: string[]
  /** The deal's `location` text was a name lookup's and the server set it aside too. */
  withheldLocation?: boolean
}

export type DossierBrokerMaster = {
  id: string
  name: string
  email: string | null
  phone: string | null
  title: string | null
  firm: string | null
  firmId: string | null
  firmWebsite: string | null
  office: string | null
  relationshipStatus: string | null
  isPreferred: boolean
  isBlocklisted: boolean
  /** Null until scored — an unscored broker is not a zero-quality one. */
  qualityScore: number | null
  totalSubmissions: number
  fundedDeals: number
}

export type DossierSponsorMaster = {
  id: string
  name: string
  entityType: string | null
  contactEmail: string | null
  contactPhone: string | null
  netWorth: number | null
  liquidity: number | null
  creditScore: number | null
  yearsExperience: number | null
  trackRecord: string | null
  assetClasses: string[]
  markets: string[]
  flags: string[]
  notes: string | null
}

export type SponsorPortfolioDeal = {
  loanId: string
  propertyName: string
  assetType: string
  location: string
  loanAmount: number | null
  ltv: number | null
  dscr: number | null
  rate: string | null
  stage: string
  dealStatus: string | null
  createdAt: string | null
  fundedAt: string | null
  fundedAmount: number | null
  isCurrent: boolean
}

export type SponsorPortfolio = {
  sponsorName: string | null
  deals: SponsorPortfolioDeal[]
  dealCount: number
  totalVolume: number
  fundedCount?: number
  fundedVolume?: number
  avgDscr: number | null
  avgLtv: number | null
  firstDealAt: string | null
  assetTypes?: string[]
  markets?: string[]
}

export type DossierComparable = {
  id: string
  /** True when the deal's own package supplied this comp. Those are shown
   *  whatever city they are in — a sponsor picks their comps for one asset. */
  suppliedWithDeal?: boolean
  /** The attachment that stated it, and the cell each figure came from. */
  sourceDocument?: string | null
  sourceCells?: Record<string, string>
  /** Columns the sponsor's table states that this schema has no field for,
   *  under their own headings. Fifty of them on The Chelsea's rent comps. */
  sourceExtras?: Record<string, { value: string | number; cell: string }>
  propertyName: string
  address: string | null
  city: string | null
  state: string | null
  /** Geocoded once on the server; null until a geocoder places it. */
  lat?: number | null
  lng?: number | null
  submarket: string | null
  assetType: string
  yearBuilt: number | null
  unitsOrSf: number | null
  numberOfUnits: number | null
  transactionDate: string | null
  transactionType: string | null
  salePrice: number | null
  pricePerUnit: number | null
  pricePerSf: number | null
  capRate: number | null
  occupancyAtSale: number | null
  noiAtSale: number | null
  isOurDeal: boolean
  dataSource: string | null
  confidence: string | null
}

/** An open obligation on the deal. `Trigger` is a lazy relationship, so the
 *  loan routes never carried it — the dossier reads it from here. */
export type DossierCondition = {
  id: string
  type: string
  due: string
  status: string
  severity: string
  description: string | null
  resolvedAt: string | null
}

export type DossierChecklist = {
  id: string
  templateId: string | null
  propertyName: string | null
  targetClosingDate: string | null
  categories: Record<string, unknown>[]
  completionPct: number | null
  status: string | null
  createdAt: string | null
}

export type DossierContext = {
  loanId: string
  conditions: DossierCondition[]
  checklists: DossierChecklist[]
  property: DossierPropertyMaster | null
  propertyMatchedBy: DossierLinkBasis
  sponsor: DossierSponsorMaster | null
  /** The broker on this deal, as the CRM holds them. Null when the deal has
   *  no linked broker — which is different from a broker with no details. */
  broker: DossierBrokerMaster | null
  sponsorMatchedBy: DossierLinkBasis
  sponsorPortfolio: SponsorPortfolio
  comparables: DossierComparable[]
  /** Comp tables the deal's package states that could not be read — so the
   *  panel says the package holds comps rather than that none exist. */
  packageCompTablesUnread?: { document: string; heading: string }[]
  /**
   * The site intel the server already holds for this property, when it is
   * fresh — so the dossier renders county, flood zone and nearby places with
   * the record instead of after a fan-out to Google and three government
   * services. Absent when nothing is stored yet; the dossier then asks
   * `/property-intel` once and the answer is kept for next time.
   */
  siteIntel?: PropertySiteIntel | null
  /** The deal's address by the one order every surface uses. */
  address?: DossierAddress
}

/** One external source's outcome — answered, or why it could not. */
export type IntelSource = { source: string; ok: boolean; detail: string | null }

export type NearbyPlace = {
  name: string
  vicinity: string | null
  rating: number | null
  lat: number | null
  lng: number | null
  kind: string
}

export type DemographicRing = {
  miles: number
  population: number | null
  households: number | null
  medianAge: number | null
  medianHouseholdIncome: number | null
  populationGrowthPct: number | null
  householdGrowthPct: number | null
  daytimeJobs: number | null
  blockGroupCount: number
  note: string
}

export type PropertySiteIntel = {
  addressUsed: string
  formattedAddress: string | null
  lat: number | null
  lng: number | null
  /**
   * How precisely the address text geocoded: `street` is a building,
   * `locality` is a place name's centre. Absent on older servers.
   */
  geocodePrecision?: 'street' | 'locality' | null
  /** The server declined to look: no verified street address on this deal. */
  unavailable?: boolean
  reason?: string | null
  placeId: string | null
  county: string | null
  countyFips: string | null
  stateCode: string | null
  postalCode: string | null
  neighborhood: string | null
  website: string | null
  phone: string | null
  googleMapsUrl: string | null
  placeTypes: string[]
  businessStatus: string | null
  population: number | null
  populationPrior: number | null
  populationGrowthPct: number | null
  populationGeography: string | null
  households: number | null
  householdsPrior: number | null
  householdGrowthPct: number | null
  medianAge: number | null
  medianHouseholdIncome: number | null
  floodZone: string | null
  floodZoneDetail: string | null
  transit: NearbyPlace[]
  schools: NearbyPlace[]
  /**
   * Every nearby category the backend resolved, keyed by
   * app.services.property_intel.NEARBY_KINDS. `transit` and `schools` above
   * are the same rows under their original names, kept so existing readers
   * are unaffected.
   */
  nearby: Record<string, NearbyPlace[]>
  nearbyRadiusM: number
  /** Which ring the counts were taken in — 1.5 mi for a city site, 5 mi for a suburban one. */
  nearbyDensity?: 'urban' | 'suburban' | null
  /** Places found per category. Nearby Search stops at 20: `nearbyCapped` says which hit it. */
  nearbyCounts?: Record<string, number>
  nearbyCapped?: Record<string, boolean>
  /** The parcel under the coordinate, from Regrid, as GeoJSON geometry for the map. */
  parcelGeometry?: GeoJsonGeometry | null
  parcelNumber?: string | null
  parcelOwner?: string | null
  parcelAcres?: number | null
  parcelZoning?: string | null
  parcelLandUse?: string | null
  /** The building outline under the coordinate, from OpenStreetMap — drawn when no parcel is on record. */
  buildingFootprint?: GeoJsonGeometry | null
  buildingLevels?: number | null
  buildingName?: string | null
  /**
   * Cumulative 1/2/3-mile ACS block-group estimates. Labeled as estimates —
   * ACS does not publish a radius. Daytime jobs stay null until LEHD LODES
   * is queried off the request path.
   */
  rings?: DemographicRing[]
  sources: IntelSource[]
}

export type GeoJsonGeometry = {
  type: 'Polygon' | 'MultiPolygon'
  coordinates: unknown
}

/** Category order and labels for the neighbourhood block. */
export const NEARBY_CATEGORY_LABELS: Array<{ kind: string; label: string }> = [
  { kind: 'grocery', label: 'Grocery' },
  { kind: 'dining', label: 'Dining' },
  { kind: 'retail', label: 'Retail' },
  { kind: 'pharmacy', label: 'Pharmacy' },
  { kind: 'transit', label: 'Transit' },
  { kind: 'school', label: 'Schools' },
  { kind: 'university', label: 'Universities' },
  { kind: 'healthcare', label: 'Healthcare' },
  { kind: 'park', label: 'Parks' },
]

/** A lexicon count over what was written on the deal — never a model's opinion. */
export type JournalSentiment = {
  positive: number
  neutral: number
  negative: number
  label: string
  asOf: string | null
  itemsScored: number
  sources: Record<string, number>
  method: string
}

/** A fact computed from one structured record, which it names. */
export type JournalInsightRecord = {
  text: string
  source: string
  tone: 'positive' | 'neutral' | 'negative'
}

/** GET /loans/{id}/journal-intelligence — see services.journal_intelligence. */
export type JournalIntelligence = {
  loanId: string
  sentiment: JournalSentiment | null
  insights: JournalInsightRecord[]
  asOf: string
}

export const dossierContextService = {
  getContext: (loanId: string) =>
    api.get(`/loans/${loanId}/dossier-context`).then((r) => r.data as DossierContext),

  /** The Journal's sentiment and insights, read on the server from the deal's own record. */
  getJournalIntelligence: (loanId: string) =>
    api.get(`/loans/${loanId}/journal-intelligence`).then((r) => r.data as JournalIntelligence),

  getSiteIntel: (params: {
    address: string
    lat?: number | null
    lng?: number | null
    /** Lets the server keep the answer on this deal's property master. */
    loanId?: string | null
  }) =>
    api
      .get('/property-intel', {
        params: {
          address: params.address,
          lat: typeof params.lat === 'number' ? params.lat : undefined,
          lng: typeof params.lng === 'number' ? params.lng : undefined,
          loan_id: params.loanId || undefined,
        },
      })
      .then((r) => r.data as PropertySiteIntel),
}


/**
 * One file that arrived on this deal, and the message it arrived on.
 *
 * A deal is a conversation: the reply with the rent roll and the forward
 * carrying the debt model are each their own intake record. Reading only the
 * record the deal was created from showed the first email and hid every one
 * after it.
 */
export interface DealCorrespondenceFile {
  filename: string
  /** Absent for an attachment the parser saw but could not store. */
  sha256: string | null
  kind: 'email_source' | 'attachment' | 'document'
  /** What a reader would call it — "PDF", "Excel workbook", "Original email". */
  documentType: string
  /** What the parser determined the document *is* — "term sheet", "offering
   *  memorandum", "underwriting model". "correspondence" where it could not
   *  tell, which is a real answer rather than a guess. */
  category: string
  /** Null where the package recorded no size — not an empty file. */
  sizeBytes: number | null
  /** Whether the bytes were kept. An attachment the parser saw but could not
   *  read is listed — it arrived — but there is nothing to open. */
  available: boolean
  /** The intake record this arrived on; how the source viewer addresses it. */
  ingestionId: string
  subject: string | null
  from: string | null
  fromName: string | null
  receivedAt: string | null
  /** Who the document came from: the sender, or — when the sender is the
   *  lender's own forwarder — the broker the package names. Absent from
   *  older servers. */
  party?: string | null
  /** Pages the parser read, where it recorded a count. */
  pages?: number | null
  /** The parser's role for the file ("term_sheet", "unknown"…). */
  documentRole?: string | null
}

export interface DealCorrespondenceMessage {
  ingestionId: string
  subject: string | null
  from: string | null
  fromName: string | null
  receivedAt: string | null
  sourceType: string | null
  status: string | null
  convertedToLoanId: string | null
}

export interface DealCorrespondence {
  loanId: string
  messages: DealCorrespondenceMessage[]
  files: DealCorrespondenceFile[]
  messageCount: number
  fileCount: number
}

/** Every file that ever arrived on a deal, oldest message first. */
export function getDealCorrespondence(loanId: string): Promise<DealCorrespondence> {
  return api.get(`/loans/${loanId}/correspondence`).then((r) => r.data as DealCorrespondence)
}

/**
 * Whose figure a value is.
 *
 *   sponsor       stated by a document that came from outside the firm — the
 *                 sponsor's model, the broker's summary. Evidence of what was
 *                 represented, not of what is true.
 *   client        a person here decided it, or it came from the firm's own
 *                 underwriter model. This is the diligence.
 *   unattributed  the platform cannot say, and says so rather than guessing.
 */
/** Whose statement a value is. `sponsor` only for the sponsor's own
 *  underwriting model; `broker` for the broker's message or what the broker
 *  sent; `external` for any other party outside the firm. */
export type ValueAttribution = 'sponsor' | 'broker' | 'external' | 'client' | 'unattributed'

export interface AttributedValue {
  field: string
  value: unknown
  attribution: ValueAttribution
  /** Why it is attributed this way, in words a reader can check. */
  why: string
  sourceDoc: string | null
  sourceSha256: string | null
  /** The cell in the sponsor's model, where the value came from one. */
  cellRef: string | null
  page: string | null
  method: string | null
  assertedBy: string | null
  observedAt: string | null
}

export interface DealAttribution {
  loanId: string
  entries: AttributedValue[]
  byField: Record<
    string,
    {
      sponsor: AttributedValue | null
      broker?: AttributedValue | null
      external?: AttributedValue | null
      client: AttributedValue | null
    }
  >
  sponsorFieldCount: number
  brokerFieldCount?: number
  externalFieldCount?: number
  clientFieldCount: number
  /** Fields where the firm's conclusion and the sponsor's assertion differ —
   *  the list the comparison exists to produce. */
  disputedFields: string[]
}

/** Who states each value on this deal — the sponsor, or the firm itself. */
export function getDealAttribution(loanId: string): Promise<DealAttribution> {
  return api.get(`/loans/${loanId}/attribution`).then((r) => r.data as DealAttribution)
}
